/*
  Stages tab in read mode (UI overhaul wave 2, screen 04 idea 4; phone layout idea 10).

  - StagesList        — one row per stage: number / name / "3 items · A. Original BOQ · …" / status pill /
                        waiting pill / "View file" / ⋯. Clicking the stage name opens the stage drawer; a click
                        anywhere else on the row expands / collapses its items. Nothing on the
                        list is an input, so reading can never change data by accident.
  - StageDotsStepper  — the small dot pipeline of the phone layout ("Next: 03 Material Submittal").
  - StageWaitingCard  — the amber "Pre-Qualification · 1 item — Pending from sales" card on phones.
  Wave 3 (idea 8): each open stage shows its clock ("Submitted 22 d · SLA 14 d", red past the SLA); an approval
  gate shows whom it waits for (29 #6, "Waiting for Consultant").
  Final QA: one line per stage only when every row fits on one line — the stage name in full (never under 160px),
  then all its pills and the file link — measured from the rendered rows (measureOneLineWidth), and never below
  ONE_LINE_MIN_WIDTH. Otherwise the whole list puts the pills on a second line under the name, so a name is never
  cut and a pill never wraps beside it (1440 with the overview column beside the list: "Material Submittal" with
  "Not Started · Approval · Consultant · Waiting 28 d · SLA 14 d · View file"). Phone cards
  get "1 item · View file": the stage name is the card's (stretched) button and the file link sits above it —
  never a button inside a button. Expanding a stage lists its items, each with its own "View file" when it has one;
  "View N files" on the list expands the stage the same way.
*/
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, ChevronsUpDown, Eye, FileText, FolderOpen, Hourglass, Plus, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { Segmented } from '@/components/ui/segmented';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { getStatusTone } from '@/lib/status';
import { MONO, Panel, useElementWidth } from './ProjectUi';
import { isStageDone, stageDisplayStatus, stageNumber } from './projectDetailModel';
import { describeStageClock, stageRowState, summarizeStageItems } from './stagesModel';

// A list narrower than this always puts the pills under the name (tablets, laptops with the side column beside it).
const ONE_LINE_MIN_WIDTH = 720;
// In the one-line layout the name column never gets less than this.
const NAME_MIN_WIDTH = 160;

const px = (value) => parseFloat(value) || 0;
const text = (value) => String(value || '').trim();
const clippedBy = (node) => Math.max(0, node.scrollWidth - node.clientWidth);

// What an element needs to show in full: its box plus whatever it (the truncating name) or a pill label clips.
function naturalWidth(el) {
  let width = el.getBoundingClientRect().width;
  if (el.matches('[data-stage-open]')) width += clippedBy(el);
  el.querySelectorAll('.ui-status-label').forEach((label) => { width += clippedBy(label); });
  return width;
}

function sumWithGaps(container) {
  if (!container) return 0;
  const children = [...container.children].filter((el) => el.getBoundingClientRect().width > 0);
  const gap = px(window.getComputedStyle(container).columnGap);
  return children.reduce((sum, el) => sum + naturalWidth(el), 0) + gap * Math.max(0, children.length - 1);
}

/*
  The panel width a stage row needs to sit on one line: the frame (padding, border), the number node, the name line
  in full (at least NAME_MIN_WIDTH), every pill and the file link side by side, the ⋯ column and the gaps between.
  Built from intrinsic widths (the name's scrollWidth, each pill plus what its label clips), so the answer is the
  same whichever layout the row is in and switching layouts cannot flip-flop.
*/
export function measureOneLineWidth(grid, panel) {
  const style = window.getComputedStyle(grid);
  const frame = panel.getBoundingClientRect().width - grid.getBoundingClientRect().width
    + px(style.paddingLeft) + px(style.paddingRight);
  const column = (name) => grid.querySelector(`[data-stage-col="${name}"]`)?.getBoundingClientRect().width || 0;
  const name = sumWithGaps(grid.querySelector('[data-stage-name-line]'));
  const pills = sumWithGaps(grid.querySelector('[data-stage-col="pills"]'));
  return frame + column('node') + Math.max(NAME_MIN_WIDTH, name) + pills + column('menu') + px(style.columnGap) * 3;
}

const RING_BY_TONE = {
  blue: 'border-status-blue text-status-blue-ink',
  amber: 'border-status-amber text-status-amber-ink',
  red: 'border-status-red text-status-red-ink',
};

function StageNode({ stage, state, number, size = 32 }) {
  const tone = getStatusTone(stageDisplayStatus(stage.status), 'stage');
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center rounded-full border text-[11.5px] font-bold tabular-nums',
        state === 'done' && 'border-status-green bg-status-green text-white',
        state === 'current' && 'border-2 border-[color:var(--brand)] bg-surface text-[color:var(--brand-ink)] shadow-[0_0_0_3px_var(--brand-soft)]',
        state === 'todo' && (RING_BY_TONE[tone] ? `border-2 bg-surface ${RING_BY_TONE[tone]}` : 'border-line-2 bg-surface text-ink-3'),
      )}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {state === 'done' ? <Check className="h-4 w-4" strokeWidth={3} /> : number}
    </span>
  );
}

function WaitingPills({ stage, waiting = [] }) {
  return waiting.map((entry) => (
    <StatusPill
      key={entry.key}
      tone="amber"
      size="sm"
      label={entry.count > 1 ? `${entry.status} · ${entry.count}` : entry.status}
      data-testid={`stage-waiting-${stage.id}-${entry.key}`}
    />
  ));
}

/*
  Who an approval gate waits for (29 #6 — the approver set in Admin › Stage templates; GET /projects/{id} gives
  `approved_by_label`, the template's for projects created before approvers existed). Submitted: "Waiting for
  Consultant" (amber); still open: "Approval · Consultant". Nothing once the stage is approved.
*/
function ApproverPill({ stage }) {
  const label = String(stage.approved_by_label || '').trim();
  if (!stage.required_approval || !label || isStageDone(stage.status)) return null;
  const submitted = /submitted|awaiting/i.test(String(stage.status || ''));
  return (
    <StatusPill
      tone={submitted ? 'amber' : 'gray'}
      size="sm"
      hideDot={!submitted}
      label={submitted ? `Waiting for ${label}` : `Approval · ${label}`}
      title={`${stage.name} needs approval by ${label}`}
      data-testid={`stage-approver-${stage.id}`}
    />
  );
}

function ClockPill({ stage, clock }) {
  const info = describeStageClock(clock, stage.name);
  if (!info) return null;
  return (
    <StatusPill
      tone={info.tone}
      size="sm"
      icon={Timer}
      label={info.chip}
      title={info.sentence}
      aria-label={info.sentence}
      data-testid={`stage-row-clock-${stage.id}`}
    />
  );
}

// Expanded stage: one line per item; an item with its own file gets "View file", which opens that file directly.
function ItemPreview({ items, onOpenReference }) {
  if (!items.length) return null;
  return (
    <ul className="mt-2 space-y-1.5 border-l-2 border-line-2 pl-3" aria-label="Items">
      {items.map((line) => {
        const reference = text(line.reference);
        return (
          <li key={line.id} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
            <span className="min-w-0 truncate font-semibold text-ink">{line.name || 'Untitled item'}</span>
            <StatusPill status={line.status || 'In Progress'} domain="stage_line" size="sm" />
            {line.date ? <span className="text-ink-3">{formatDate(line.date)}</span> : null}
            {(line.sub_items || []).length ? <span className="text-ink-3">{line.sub_items.length} sub-item{line.sub_items.length === 1 ? '' : 's'}</span> : null}
            {reference ? (
              <button
                type="button"
                onClick={() => onOpenReference?.(reference)}
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`stage-item-file-${line.id}`}
              >
                <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                View file
                <span className="sr-only">{` of ${line.name || 'this item'}`}</span>
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function StagesList({
  stages = [],
  linesByStage = {},
  waitingByStage = {},
  nextStageId,
  onOpen,
  onAddItem,
  onOpenReference,
  menuFor,
  highlightedStageId,
  canEdit = true,
  phone = false,
  clocks = {},
  folderFor,
}) {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const panelRef = useRef(null);
  const panelWidth = useElementWidth(panelRef);
  // Widest one-line need of any stage, measured below; kept per stage so the Open / Done filters keep the layout.
  const [oneLineNeed, setOneLineNeed] = useState(0);
  const needByStage = useRef(new Map());
  const [, setFontsLoaded] = useState(false);
  const oneLine = panelWidth === null || panelWidth >= Math.max(ONE_LINE_MIN_WIDTH, oneLineNeed);
  const rows = useMemo(() => stages.map((stage, index) => ({
    stage,
    index,
    number: stageNumber(stage, index),
    state: stageRowState(stage, nextStageId),
    summary: summarizeStageItems(linesByStage[stage.id] || stage.lines || []),
  })), [stages, linesByStage, nextStageId]);

  // Web fonts change every width: measure again once they are in.
  useEffect(() => {
    let alive = true;
    document.fonts?.ready?.then(() => { if (alive) setFontsLoaded(true); });
    return () => { alive = false; };
  }, []);
  // After every render (pills, clocks and names change with the data), before paint: re-measure the shown rows.
  // No dependency list on purpose; the state only changes when the measured need changes, so it settles.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (phone || !panel || panelWidth === null) return;
    panel.querySelectorAll('[data-stage-grid]').forEach((grid) => {
      needByStage.current.set(grid.getAttribute('data-stage-grid'), measureOneLineWidth(grid, panel));
    });
    const need = Math.ceil(Math.max(0, ...rows.map((row) => needByStage.current.get(String(row.stage.id)) || 0)));
    if (need !== oneLineNeed) setOneLineNeed(need);
  });
  const doneCount = rows.filter((row) => row.state === 'done').length;
  const visible = rows.filter((row) => (filter === 'all' ? true : filter === 'done' ? row.state === 'done' : row.state !== 'done'));
  const allExpanded = visible.length > 0 && visible.every((row) => expanded.has(row.stage.id) || !row.summary.count);
  const toggle = (stageId) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(stageId)) next.delete(stageId); else next.add(stageId);
    return next;
  });

  const fileLink = (row) => {
    const { references, count } = row.summary;
    if (!references.length) {
      // 06 #5: a stage with no file of its own links to its OneDrive folder (from the last scan).
      const folder = folderFor?.(row.stage);
      if (!folder?.webUrl) return null;
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenReference?.(folder.webUrl);
          }}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded text-[12.5px] font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={`Open ${folder.name} in OneDrive`}
          data-row-click="ignore"
          data-testid={`stage-folder-${row.stage.id}`}
        >
          <FolderOpen className="h-3.5 w-3.5" aria-hidden="true" />
          Folder
          <span className="sr-only">{` ${folder.name} of ${row.stage.name}`}</span>
        </button>
      );
    }
    // Several files: on the list, expand the stage so each item shows its own "View file" (when the items carry
    // the files themselves); on phones, or when the files sit on sub-items only, open the stage drawer.
    const expands = references.length > 1 && !phone && row.summary.items.some((line) => text(line.reference));
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (references.length === 1) onOpenReference?.(references[0]);
          else if (expands) toggle(row.stage.id);
          else onOpen?.(row.stage);
        }}
        aria-expanded={expands ? expanded.has(row.stage.id) : undefined}
        className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded text-[12.5px] font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-row-click="ignore"
        data-testid={`stage-files-${row.stage.id}`}
      >
        <FileText className="h-3.5 w-3.5" aria-hidden="true" />
        {references.length === 1 ? 'View file' : `View ${references.length} files`}
        <span className="sr-only">{count ? ` of ${row.stage.name}` : ''}</span>
      </button>
    );
  };

  if (phone) {
    return (
      <ol className="space-y-2.5" data-testid="stages-list" aria-label="All stages">
        {rows.map((row) => {
          const { stage, state, number, summary } = row;
          const waiting = waitingByStage[stage.id] || [];
          const file = fileLink(row);
          return (
            <li key={stage.id} id={`stage-card-${stage.id}`} className="scroll-mt-24">
              <div
                className={cn(
                  'relative flex min-h-[60px] w-full items-center gap-3 rounded-[14px] border border-line bg-surface px-3.5 py-2.5 text-left shadow-card transition-colors active:bg-surface-hover',
                  state === 'current' && 'border-[color:var(--brand)] bg-brand-soft shadow-[inset_3px_0_0_var(--brand)]',
                  highlightedStageId === stage.id && 'ring-2 ring-[color:var(--brand)] ring-offset-2',
                )}
                data-testid={`stage-row-${stage.id}`}
              >
                <StageNode stage={stage} state={state} number={number} />
                <span className="min-w-0 flex-1">
                  {/* the whole card is this button (its ::after covers the card); the file link sits above it */}
                  <button
                    type="button"
                    data-stage-open
                    onClick={() => onOpen?.(stage)}
                    className={cn(
                      'block w-full truncate text-left text-[15px] font-bold outline-none after:absolute after:inset-0 after:rounded-[14px] focus-visible:after:ring-2 focus-visible:after:ring-ring',
                      state === 'current' ? 'text-[color:var(--brand-ink)]' : 'text-ink',
                    )}
                    aria-label={`${stage.name}: ${stageDisplayStatus(stage.status)} · ${summary.subtitle}. Open to ${canEdit ? 'update' : 'view'}`}
                  >
                    {stage.name}
                  </button>
                  <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-3">
                    <span>{summary.count ? `${summary.count} item${summary.count === 1 ? '' : 's'}` : 'No items yet'}</span>
                    {file ? <><span aria-hidden="true">·</span><span className="relative z-[1]">{file}</span></> : null}
                    <WaitingPills stage={stage} waiting={waiting} />
                    <ApproverPill stage={stage} />
                    <ClockPill stage={stage} clock={clocks[stage.id]} />
                  </span>
                </span>
                <StatusPill status={stageDisplayStatus(stage.status)} domain="stage" size="sm" />
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-4" aria-hidden="true" />
              </div>
            </li>
          );
        })}
      </ol>
    );
  }

  return (
    <Panel
      ref={panelRef}
      className="overflow-hidden"
      data-testid="stages-list"
      data-layout={oneLine ? 'one-line' : 'two-line'}
      data-one-line-from={Math.max(ONE_LINE_MIN_WIDTH, oneLineNeed)}
    >
      <div className="flex min-h-[56px] flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-2.5 sm:px-5">
        <h3 className="text-[14px] font-bold text-ink">All stages</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            size="sm"
            ariaLabel="Which stages"
            value={filter}
            onChange={setFilter}
            options={[
              { key: 'all', label: 'All', count: rows.length },
              { key: 'open', label: 'Open', count: rows.length - doneCount },
              { key: 'done', label: 'Done', count: doneCount },
            ]}
            testId="stages-filter"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(allExpanded ? new Set() : new Set(visible.map((row) => row.stage.id)))}
            aria-pressed={allExpanded}
          >
            <ChevronsUpDown aria-hidden="true" />
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </div>
      </div>
      <ol>
        {visible.map((row) => {
          const { stage, state, number, summary } = row;
          const waiting = waitingByStage[stage.id] || [];
          const isOpen = expanded.has(stage.id) && summary.count > 0;
          const menuItems = menuFor?.(stage, summary) || [];
          return (
            <li
              key={stage.id}
              id={`stage-card-${stage.id}`}
              className={cn(
                'group relative scroll-mt-24 border-b border-line-2 transition-colors last:border-b-0',
                state === 'current' && 'bg-brand-soft before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-[color:var(--brand)]',
                highlightedStageId === stage.id && 'ring-2 ring-inset ring-[color:var(--brand)]',
              )}
              data-testid={`stage-row-${stage.id}`}
              data-state={state}
              onClick={(event) => {
                // Owner request: a click on empty space in the row (or its items) expands / collapses the stage;
                // only the stage name opens the drawer. The chevron stays the keyboard target for expanding.
                if (event.target.closest('button, a, input, [data-row-click="ignore"]')) return;
                if (summary.count) toggle(stage.id);
              }}
            >
              <div
                className={cn(
                  'grid min-h-[60px] items-center gap-x-3 gap-y-1.5 px-4 py-2.5 sm:px-5',
                  summary.count > 0 && 'cursor-pointer',
                  // two-line: name on line 1, pills on line 2; one-line: the name keeps at least NAME_MIN_WIDTH
                  oneLine ? 'grid-cols-[32px_minmax(160px,1fr)_minmax(0,auto)_32px]' : 'grid-cols-[32px_minmax(0,1fr)_32px]',
                  state !== 'current' && 'hover:bg-surface-hover',
                )}
                data-stage-grid={stage.id}
              >
                <span className={cn('self-center', !oneLine && 'row-span-2')} data-stage-col="node">
                  <StageNode stage={stage} state={state} number={number} />
                </span>
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5" data-stage-name-line>
                    <button
                      type="button"
                      data-stage-open
                      onClick={(event) => { event.stopPropagation(); onOpen?.(stage); }}
                      className={cn(
                        'min-w-0 truncate rounded text-left text-[14.5px] font-bold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        state === 'current' ? 'text-[color:var(--brand-ink)]' : 'text-ink',
                      )}
                      aria-label={`${stage.name}: ${stageDisplayStatus(stage.status)} · ${summary.subtitle}. Open to ${canEdit ? 'update' : 'view'}`}
                    >
                      {stage.name}
                    </button>
                    {summary.count ? (
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); toggle(stage.id); }}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Hide' : 'Show'} items of ${stage.name}`}
                        className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-4 hover:bg-surface-hover hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px] text-ink-3">
                    <span className="min-w-0 truncate">{summary.subtitle}</span>
                    {!summary.count && canEdit ? (
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onAddItem?.(stage); }}
                        className="inline-flex shrink-0 items-center gap-1 rounded font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid={`stage-add-item-${stage.id}`}
                      >
                        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                        Add item
                      </button>
                    ) : null}
                  </div>
                </div>
                <div
                  className={cn('flex min-w-0 flex-wrap items-center gap-2', oneLine ? 'justify-end' : 'col-start-2 row-start-2 justify-start')}
                  data-stage-col="pills"
                >
                  <StatusPill status={stageDisplayStatus(stage.status)} domain="stage" size="sm" data-testid={`stage-status-${stage.id}`} />
                  <WaitingPills stage={stage} waiting={waiting} />
                  <ApproverPill stage={stage} />
                  <ClockPill stage={stage} clock={clocks[stage.id]} />
                  {fileLink(row)}
                </div>
                <span className={cn('self-center', !oneLine && 'col-start-3 row-span-2 row-start-1')} data-stage-col="menu">
                  {menuItems.filter(Boolean).length ? (
                    <RowActions
                      size="sm"
                      label={`Actions for stage ${stage.name}`}
                      items={menuItems}
                      data-testid={`stage-actions-${stage.id}`}
                    />
                  ) : null}
                </span>
              </div>
              {isOpen ? (
                <div className="cursor-pointer pb-3 pl-[60px] pr-5 sm:pl-[64px]">
                  <ItemPreview items={summary.items} onOpenReference={onOpenReference} />
                </div>
              ) : null}
            </li>
          );
        })}
        {!visible.length ? (
          <li className="px-5 py-8 text-center text-[13px] text-ink-3">{filter === 'done' ? 'No stage is approved yet.' : 'Every stage is approved.'}</li>
        ) : null}
      </ol>
      <div className="flex items-center gap-2 border-t border-line px-4 py-2.5 text-[12.5px] text-ink-3 sm:px-5">
        <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Read mode — click a row to show its items, or a stage name to review or {canEdit ? 'edit its items in a side panel. Nothing changes until you press Save.' : 'see its items.'}
      </div>
    </Panel>
  );
}

/** Phone pipeline: one dot per stage, the next one ringed, "Next: 03 Material Submittal · 2 of 9 approved". */
export function StageDotsStepper({ stages = [], nextStage, nextNumber, approved = 0, onSelect }) {
  if (!stages.length) return null;
  const nextIndex = nextStage ? stages.findIndex((stage) => stage.id === nextStage.id) : -1;
  return (
    <Panel className="px-4 py-3.5" data-testid="stage-dots">
      <ol className="flex items-center" aria-label="Stage pipeline">
        {stages.map((stage, index) => {
          const done = isStageDone(stage.status);
          const current = index === nextIndex;
          return (
            <li key={stage.id} className={cn('flex items-center', index < stages.length - 1 && 'flex-1')}>
              <span
                className={cn(
                  'block shrink-0 rounded-full',
                  done && 'h-2.5 w-2.5 bg-status-green',
                  current && 'h-4 w-4 border-2 border-[color:var(--brand)] bg-surface',
                  !done && !current && 'h-2 w-2 bg-[color:var(--line-2)]',
                )}
                title={`${stage.name}: ${stageDisplayStatus(stage.status)}`}
              >
                <span className="sr-only">{`${stage.name}: ${stageDisplayStatus(stage.status)}${current ? ' (next)' : ''}`}</span>
              </span>
              {index < stages.length - 1 ? (
                <span aria-hidden="true" className={cn('mx-1 h-[2px] flex-1 rounded-full', done ? 'bg-status-green' : 'bg-line')} />
              ) : null}
            </li>
          );
        })}
      </ol>
      <div className="mt-2.5 flex items-center justify-between gap-3 text-[13px]">
        {nextStage ? (
          <button
            type="button"
            onClick={() => onSelect?.(nextStage)}
            className="min-w-0 truncate rounded text-left font-bold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Next: <span className={cn(MONO, 'text-[color:var(--brand-ink)]')}>{nextNumber}</span> {nextStage.name}
          </button>
        ) : <span className="font-bold text-ink">All stages approved</span>}
        <span className="shrink-0 text-ink-3">{approved} of {stages.length} approved</span>
      </div>
    </Panel>
  );
}

/** Phone: the first item someone else holds, as an amber card that opens its stage. */
export function StageWaitingCard({ waitingOn, stages = [], onOpen }) {
  const party = (waitingOn?.parties || []).find((entry) => entry.items.length);
  if (!party) return null;
  const item = party.items[0];
  const stage = stages.find((entry) => entry.id === item.stageId);
  const sameStage = party.items.filter((entry) => entry.stageId === item.stageId).length;
  return (
    <button
      type="button"
      onClick={() => stage && onOpen?.(stage)}
      className="flex w-full items-center gap-3 rounded-[14px] border border-[color:color-mix(in_srgb,var(--status-amber)_40%,transparent)] bg-status-amber-bg px-3.5 py-3 text-left"
      data-testid="stage-waiting-card"
    >
      <span className="ui-icon-tile tone-amber h-9 w-9 shrink-0" aria-hidden="true"><Hourglass className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1 text-[13.5px] leading-snug text-ink">
        <b className="font-bold">{item.stageName}</b> · {sameStage} item{sameStage === 1 ? '' : 's'}
        <span className="block text-ink-2">Pending from {party.label.toLowerCase() === 'execution team' ? 'Execution' : party.label.toLowerCase()}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
    </button>
  );
}
